import { Component, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { Router } from "@angular/router"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatIconModule } from "@angular/material/icon"
import { MatSelectModule } from "@angular/material/select"
import { PageHeaderComponent } from "../../../shared/page-header/page-header.component"
import { DataLoaderComponent } from "../../../shared/data-loader/data-loader.component"
import { AuthService } from "../../../services/auth.service"
import { SupportCategory, SupportService } from "../../../services/support.service"

@Component({
  selector: "app-support-settings",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    PageHeaderComponent,
    DataLoaderComponent,
  ],
  templateUrl: "./support-settings.component.html",
  styleUrl: "./support-settings.component.css",
})
export class SupportSettingsComponent implements OnInit {
  categories: SupportCategory[] = []
  loading = false
  showAddForm = false
  editingCategory: SupportCategory | null = null

  newCategory = {
    value: "",
    label: "",
    description: "",
    routes_to: "admin",
    sort_order: 0,
  }

  isSaving = false
  formError = ""

  constructor(
    private supportService: SupportService,
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.authService.getCurrentUserInfo()?.role?.toLowerCase() !== "admin") {
      this.router.navigate(["/help/tickets"])
      return
    }
    this.loadCategories()
  }

  loadCategories(): void {
    this.loading = true
    this.supportService.getSettingsCategories().subscribe({
      next: (categories) => {
        this.categories = categories ?? []
        this.loading = false
      },
      error: () => {
        this.categories = []
        this.loading = false
      },
    })
  }

  onCategoryKeyInput(event: Event): void {
    const input = event.target as HTMLInputElement
    const next = (input.value || "").toLowerCase().replace(/\s+/g, "_")
    this.newCategory.value = next
    input.value = next
  }

  saveNewCategory(): void {
    this.formError = ""
    const value = this.newCategory.value.trim()
    const label = this.newCategory.label.trim()
    if (!value || !label) {
      this.formError = "Category key and label are required."
      return
    }
    if (!/^[a-z0-9_]+$/.test(value)) {
      this.formError = "Category key must be lowercase letters, numbers, and underscores only."
      return
    }

    this.isSaving = true
    this.supportService
      .createCategory({
        value,
        label,
        description: this.newCategory.description.trim(),
        routes_to: this.newCategory.routes_to,
        sort_order: this.newCategory.sort_order || this.categories.length,
      })
      .subscribe({
        next: () => {
          this.isSaving = false
          this.showAddForm = false
          this.formError = ""
          this.newCategory = {
            value: "",
            label: "",
            description: "",
            routes_to: "admin",
            sort_order: 0,
          }
          this.loadCategories()
        },
        error: (err) => {
          this.isSaving = false
          this.formError =
            err?.error?.error || err?.error?.message || "Unable to save category."
        },
      })
  }

  saveEditCategory(): void {
    if (!this.editingCategory?.id) return
    this.isSaving = true
    this.supportService
      .updateCategory(this.editingCategory.id, {
        label: this.editingCategory.label,
        description: this.editingCategory.description,
        routes_to: this.editingCategory.routes_to,
      })
      .subscribe({
        next: (res) => {
          this.isSaving = false
          this.applyCategoryResponse(res)
          this.editingCategory = null
        },
        error: () => {
          this.isSaving = false
        },
      })
  }

  toggleCategoryActive(cat: SupportCategory): void {
    if (cat.id == null) return
    const nextActive = !this.isCategoryActive(cat)
    cat.is_active = nextActive
    this.supportService.updateCategory(cat.id, { is_active: nextActive }).subscribe({
      next: (res) => this.applyCategoryResponse(res, cat.id),
      error: () => {
        cat.is_active = !nextActive
        this.loadCategories()
      },
    })
  }

  isCategoryActive(cat: SupportCategory): boolean {
    return this.supportService.toBooleanFlag(cat.is_active)
  }

  private applyCategoryResponse(res: unknown, fallbackId?: number): void {
    const obj = (res && typeof res === "object" ? res : {}) as Record<string, unknown>
    const raw = obj["category"] ?? obj["data"]
    if (!raw || typeof raw !== "object") {
      this.loadCategories()
      return
    }
    const updated = this.supportService.normalizeCategory(raw)
    const id = updated.id ?? fallbackId
    const index = this.categories.findIndex((c) => c.id === id)
    if (index >= 0) {
      this.categories[index] = { ...this.categories[index], ...updated }
      return
    }
    this.loadCategories()
  }

  startEdit(cat: SupportCategory): void {
    this.editingCategory = { ...cat }
  }
}
